import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import DialogFooterBar from './DialogFooterBar';

/**
 * The sticky footer of a record dialog: Abbrechen and Speichern, ruled off from
 * the scrolling body and always in reach.
 *
 * The Save button is the form's `submit` by default — the footer sits INSIDE the
 * `<Box component="form">`, so it needs no handler of its own; Ctrl+Enter
 * submits the same way (FormDialog). An editor with no form to submit (the sketch, whose
 * "values" are a canvas) passes `onSave` instead.
 *
 * `startSlot` takes an action that belongs to the record rather than to the
 * editing (the Befund and Tagebuch sheets' "als PDF"); it is pushed to the far
 * left, away from the two that end the dialog.
 */
export default function FormDialogFooter({
    onCancel,
    isSubmitting,
    onSave,
    saveDisabled = false,
    startSlot
}: {
    onCancel: () => void;
    /** Disables Save and turns its label into "Speichern …". */
    isSubmitting: boolean;
    /** Saves on click rather than by submitting the surrounding form. */
    onSave?: () => void;
    /** Blocks Save for a reason of the editor's own (nothing to save yet). */
    saveDisabled?: boolean;
    startSlot?: ReactNode;
}) {
    return (
        <DialogFooterBar>
            {startSlot}
            <Button
                variant="outlined"
                startIcon={<CloseIcon />}
                onClick={onCancel}
            >
                <FormattedMessage id="common.cancel" />
            </Button>
            <Button
                type={onSave ? 'button' : 'submit'}
                onClick={onSave}
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={isSubmitting || saveDisabled}
            >
                <FormattedMessage
                    id={isSubmitting ? 'common.saving' : 'common.save'}
                />
            </Button>
        </DialogFooterBar>
    );
}
